import { IconFileExport, IconSearch } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Button, Checkbox, Group, Loader, Modal, Table, Text, TextInput } from '@mantine/core';
import { useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import { FilterListByName } from '../../constants/filterListByName';

const CustomFieldExportModal = ({ show, close, segment, onExport, fieldScope = 'profile', title, description }) => {
  const { accountId, domainId } = useParams();
  const fetch = useFetch();

  const [customFields, setCustomFields] = useState([]);
  const [filteredFields, setFilteredFields] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch custom fields from API
  useEffect(() => {
    if (show) {
      setLoading(true);
      setError(null);

      fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/customFields`)
        .then((response) => {
          // Extract custom fields from response based on scope
          const profileFields = response.customFields?.[fieldScope] || [];

          // Transform custom fields to include selection state
          const fields = profileFields.map((field) => ({
            id: `${field.type}.${field.key}`,
            name: `custom.${field.type}.${field.key}`,
            type: field.type,
            key: field.key,
            selected: false,
          }));
          setCustomFields(fields);
          setFilteredFields(fields);
          setLoading(false);
        })
        .catch((err) => {
          setError(err.message || 'Failed to load custom fields');
          setLoading(false);
        });
    }
  }, [show, accountId, domainId, fetch, fieldScope]);

  // Update select all state based on selected fields
  useEffect(() => {
    if (filteredFields.length > 0) {
      setSelectAll(filteredFields.every((field) => field.selected));
    }
  }, [filteredFields]);

  const handleExport = () => {
    const selectedFields = customFields
      .filter((field) => field.selected)
      .map((field) => ({
        type: field.type,
        key: field.key,
      }));

    onExport(selectedFields);
    close();
  };

  const handleSearch = (term) => {
    if (term === '') {
      setFilteredFields(customFields);
    } else {
      setFilteredFields(FilterListByName(term, customFields));
    }
  };

  const handleSelectField = (id) => {
    setCustomFields((prev) =>
      prev.map((field) =>
        field.id === id ? { ...field, selected: !field.selected } : field
      )
    );
    setFilteredFields((prev) =>
      prev.map((field) =>
        field.id === id ? { ...field, selected: !field.selected } : field
      )
    );
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    // Get IDs of currently filtered fields
    const filteredIds = new Set(filteredFields.map((f) => f.id));

    // Update only the filtered fields
    setCustomFields((prev) =>
      prev.map((field) =>
        filteredIds.has(field.id) ? { ...field, selected: newSelectAll } : field
      )
    );
    setFilteredFields((prev) =>
      prev.map((field) => ({ ...field, selected: newSelectAll }))
    );
  };

  const hasSelectedFields = customFields.some((field) => field.selected);

  const fieldRows = filteredFields.map((field) => (
    <Table.Tr key={field.id}>
      <Table.Td>
        <Checkbox
          checked={field.selected}
          onChange={() => handleSelectField(field.id)}
        />
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Text>{field.name}</Text>
          <Text size="xs" c="dimmed">
            ({field.type})
          </Text>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Modal opened={show} onClose={close} title={title || 'Export with Custom Fields'} size="lg">
      {loading ? (
        <Group justify="center" p="xl">
          <Loader size="md" />
          <Text>Loading custom fields...</Text>
        </Group>
      ) : error ? (
        <Text c="red" ta="center" p="xl">
          {error}
        </Text>
      ) : customFields.length === 0 ? (
        <Text c="dimmed" ta="center" p="xl">
          No custom fields found for {fieldScope}s. The export will include standard columns only.
        </Text>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="md">
            {description || 'Select custom fields to include in your export. Standard columns will always be included.'}
          </Text>

          <TextInput
            placeholder="Search custom fields..."
            rightSection={<IconSearch size={16} />}
            onChange={(event) => handleSearch(event.target.value)}
            mb="md"
          />

          <Table withTableBorder highlightOnHover style={{ maxHeight: '400px', overflow: 'auto' }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '50px' }}>
                  <Checkbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    label="Select all"
                  />
                </Table.Th>
                <Table.Th>Field Name</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {fieldRows.length > 0 ? (
                fieldRows
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={2}>
                    <Text c="dimmed" ta="center">
                      No fields match your search
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </>
      )}

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={close}>
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          rightSection={<IconFileExport size={16} />}
          disabled={loading}
        >
          {hasSelectedFields ? `Export with ${customFields.filter((f) => f.selected).length} custom field${customFields.filter((f) => f.selected).length === 1 ? '' : 's'}` : 'Export (standard fields only)'}
        </Button>
      </Group>
    </Modal>
  );
};

export default CustomFieldExportModal;
